import { type ReactNode, useState } from "react";
import { Alert, Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material";
import { Money, formatCRC } from "@despejo/domain";
import { formatDateEs } from "@despejo/ui";

/** One row behind a figure: a ledger line, a payment, a prior return or a parameter. */
export interface DrillRow {
  ref: string;
  date?: string | null;
  label: string;
  detail?: string | null;
  amount: string;
}

export interface DrillSpec {
  title: string;
  /** The figure the rows explain; the dialog checks that they add up to it. */
  total: string;
  rows: DrillRow[];
  /** How the figure is formed, shown above the rows (e.g. "Ingresos − deducciones"). */
  explanation?: string;
  /** Set when the rows are inputs of a formula rather than addends (no sum check). */
  noSum?: boolean;
}

/**
 * T6: every figure is clickable and opens the rows that make it up. The
 * footer shows the rows' sum next to the figure, so a mismatch is visible.
 */
export function DrillDownDialog({ spec, onClose }: { spec: DrillSpec | null; onClose: () => void }) {
  const sum = spec ? Money.sum(spec.rows.map((r) => Money.of(r.amount))) : Money.zero();
  const matches = spec ? sum.round(2).eq(Money.of(spec.total).round(2)) : true;
  return (
    <Dialog open={spec !== null} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{spec?.title}</DialogTitle>
      <DialogContent>
        {spec?.explanation && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {spec.explanation}
          </Typography>
        )}
        {spec && spec.rows.length === 0 ? (
          <Typography variant="body2">No hay filas detrás de esta cifra.</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Concepto</TableCell>
                  <TableCell align="right">Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {spec?.rows.map((r, i) => (
                  <TableRow key={`${r.ref}-${i}`}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.date ? formatDateEs(r.date) : "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.label}</Typography>
                      {r.detail && (
                        <Typography variant="caption" color="text.secondary">
                          {r.detail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatCRC(Money.of(r.amount))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {spec && !spec.noSum && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2}>Suma de {spec.rows.length} fila(s)</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatCRC(sum)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Box>
        )}
        {spec && !spec.noSum && spec.rows.length > 0 && !matches && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Las filas suman {formatCRC(sum)} y la cifra es {formatCRC(Money.of(spec.total))}: la diferencia viene de redondeos o de una proyección.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

/** A money figure rendered as a button that opens its drill-down. */
export function DrillFigure({ amount, onOpen, label }: { amount: string; onOpen: () => void; label: string }) {
  return (
    <ButtonBase onClick={onOpen} aria-label={`Ver detalle de ${label}`} sx={{ borderRadius: 1, px: 0.5, textDecoration: "underline dotted", textUnderlineOffset: 3, font: "inherit" }}>
      {formatCRC(Money.of(amount))}
    </ButtonBase>
  );
}

/** State helper: `open(spec)` shows the dialog, `dialog` renders it. */
export function useDrillDown(): { open: (s: DrillSpec) => void; dialog: ReactNode } {
  const [spec, setSpec] = useState<DrillSpec | null>(null);
  return { open: setSpec, dialog: <DrillDownDialog spec={spec} onClose={() => setSpec(null)} /> };
}
